/** Per-image editor drawer: caption, video_url, 360°, (itinerary) show_in_group,
 *  plus Replace and Crop/Rotate — both of which produce a File the parent sends
 *  through `api.replace`. Metadata Save calls `api.update`. RHF+zod for the text
 *  fields; the 360/show_in_group switches are RHF-controlled booleans.
 *
 *  Unsaved details are never dropped silently (C21): a backdrop click, Esc, ✕ or Close
 *  with edits asks first, inline at the top of the drawer — "Discard your changes to this
 *  image's details?" · Keep editing · Discard (the immediate-action dialog's rule,
 *  DESIGN.md). A refetch of the same image (after a Save, a Replace or a crop) re-seeds only
 *  the fields the operator has not changed; another image starts clean.
 *
 *  The details save to the image at once, never into the record's draft — beside the dock's
 *  "Save N changes ⌘S" they must not look alike (DESIGN.md "The Draft-Or-Now Rule"): a bolt
 *  line under the title says so, and the button is **Save details now**
 *  (`ImmediateActionButton`). */
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CropRotateIcon from '@mui/icons-material/CropRotate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { FieldActionsAdornment } from '@/components/ValueActions/FieldActionsAdornment';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { externalUrl, looksLikeUrl } from '@/utils/externalUrl';
import { imageUrl, IMAGE_PRESET } from '@/utils/imageUrl';

import { ImageCropDialog } from './ImageCropDialog';
import { UploadDropzone } from './UploadDropzone';
import type { GalleryImage, GalleryPatch } from './types';

const schema = z.object({
  caption: z.string().max(100, 'Keep captions to 100 characters or fewer.'),
  // Allow empty; otherwise require something url-ish (has a dot, no spaces). The
  // backend does the authoritative validation — this is a light guard.
  video_url: z
    .string()
    .trim()
    .refine((v) => v === '' || (/^[^\s]+\.[^\s]+$/.test(v)), 'Enter a valid URL or leave blank.'),
  is_360_degree: z.boolean(),
  offset_360_degree: z.number().int().min(0).max(360),
  show_in_group: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

/** An editable web address: opened only when it looks like one, and always
 *  absolute — this field accepts `youtube.com/watch?v=x`, and handing that to
 *  `act.external` raw would render a router link the SPA 404s on. */
function urlActions(value: string | null | undefined, label: string): ValueAction[] {
  if (!looksLikeUrl(value)) return [];
  const v = value.trim();
  return [act.external(externalUrl(v), label), act.copy(v, 'Copy URL')];
}

function toDefaults(img: GalleryImage): FormValues {
  return {
    caption: img.caption ?? '',
    video_url: img.video_url ?? '',
    is_360_degree: img.is_360_degree,
    offset_360_degree: img.offset_360_degree ?? 0,
    show_in_group: img.show_in_group ?? false,
  };
}

const FIELDS = ['caption', 'video_url', 'is_360_degree', 'offset_360_degree', 'show_in_group'] as const;

/** The fields whose value differs from what the form was seeded with: the operator's edits. */
export function editedFields(seeded: FormValues, current: FormValues): (keyof FormValues)[] {
  return FIELDS.filter((k) => current[k] !== seeded[k]);
}

/** The inline question a close with unsaved details asks. */
export const DISCARD_QUESTION = 'Discard your changes to this image’s details?';

/** The bolt line under the title: the details are an immediate action, not a draft edit. */
export const IMAGE_DETAILS_IMMEDIATE = 'Saved to the image at once, not part of the record’s draft.';

interface Props {
  image: GalleryImage | null;
  open: boolean;
  canManage: boolean;
  showInGroup: boolean;
  saving: boolean;
  replacing: boolean;
  onClose: () => void;
  onSave: (patch: GalleryPatch) => void;
  onReplace: (file: File) => void;
}

export function ImageDetailDrawer({
  image,
  open,
  canManage,
  showInGroup,
  saving,
  replacing,
  onClose,
  onSave,
  onReplace,
}: Props) {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    setFocus,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: image ? toDefaults(image) : toDefaults({} as GalleryImage),
  });

  // What the form was last seeded with, and for which image: a refetch of the same image
  // (after a save, a replace, a crop) keeps the fields the operator changed and takes the
  // server's values for the rest; another image — or the same one opened again after the
  // drawer closed (`image` goes null) — starts clean.
  const seeded = useRef<{ id: number; values: FormValues } | null>(null);
  useEffect(() => {
    if (!image) {
      seeded.current = null;
      return;
    }
    const next = toDefaults(image);
    const prev = seeded.current;
    const current = getValues();
    const kept = prev && prev.id === image.id ? editedFields(prev.values, current) : [];
    reset(next);
    for (const k of kept) setValue(k, current[k], { shouldDirty: true });
    seeded.current = { id: image.id, values: next };
  }, [image, reset, getValues, setValue]);

  // A close with unsaved details asks first (C21), whichever way it came: the backdrop, Esc,
  // ✕ or Close.
  const [asking, setAsking] = useState(false);
  const keepRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open) setAsking(false);
  }, [open]);
  useEffect(() => {
    if (asking) keepRef.current?.focus();
  }, [asking]);
  const unsaved = canManage && isDirty;
  const requestClose = () => {
    if (unsaved) setAsking(true);
    else onClose();
  };
  const keepEditing = () => {
    setAsking(false);
    setFocus('caption');
  };
  const discard = () => {
    setAsking(false);
    if (image) reset(toDefaults(image));
    onClose();
  };

  const preview = image ? imageUrl(image.image, IMAGE_PRESET.DETAIL) : null;
  const original = image ? imageUrl(image.image) : null;
  const is360 = watch('is_360_degree');

  const submit = handleSubmit((v) => {
    const patch: GalleryPatch = {
      caption: v.caption === '' ? null : v.caption,
      video_url: v.video_url === '' ? null : v.video_url,
      is_360_degree: v.is_360_degree,
      offset_360_degree: v.offset_360_degree,
    };
    if (showInGroup) patch.show_in_group = v.show_in_group;
    onSave(patch);
  });

  return (
    <Drawer anchor="right" open={open} onClose={requestClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <Box sx={{ p: 2 }} component="form" onSubmit={submit}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Image details
          </Typography>
          <IconButton aria-label="Close" onClick={requestClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
        {canManage && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: -0.5, mb: 1.5 }}
            data-image-immediate-note
          >
            <BoltOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
            {IMAGE_DETAILS_IMMEDIATE}
          </Typography>
        )}

        {asking && (
          <Alert
            severity="warning"
            role="alertdialog"
            aria-label={DISCARD_QUESTION}
            sx={{ mb: 1.5, alignItems: 'center' }}
            data-image-discard
            action={
              <Stack direction="row" spacing={0.5}>
                <Button ref={keepRef} color="inherit" size="small" onClick={keepEditing}>
                  Keep editing
                </Button>
                <Button color="inherit" size="small" onClick={discard}>
                  Discard
                </Button>
              </Stack>
            }
          >
            {DISCARD_QUESTION}
          </Alert>
        )}

        {preview ? (
          <MuiLink href={original ?? undefined} target="_blank" rel="noopener noreferrer" sx={{ display: 'block' }}>
            <Box
              component="img"
              src={preview}
              alt={image?.caption ?? 'Image'}
              sx={{ width: '100%', borderRadius: 1, objectFit: 'cover', maxHeight: 220 }}
            />
          </MuiLink>
        ) : (
          <Box sx={{ height: 160, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', placeItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No preview
            </Typography>
          </Box>
        )}

        {original && (
          <MuiLink
            href={original}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1 }}
          >
            Open original
            <OpenInNewIcon sx={{ fontSize: 14 }} />
          </MuiLink>
        )}

        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label="Caption"
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={!canManage}
            slotProps={{ htmlInput: { maxLength: 100 } }}
            error={Boolean(errors.caption)}
            helperText={errors.caption?.message ?? `${watch('caption')?.length ?? 0}/100`}
            {...register('caption')}
          />
          <TextField
            label="Video URL"
            size="small"
            fullWidth
            placeholder="https://…"
            disabled={!canManage}
            error={Boolean(errors.video_url)}
            helperText={errors.video_url?.message ?? 'Optional link to a video.'}
            InputProps={{
              endAdornment: (
                <FieldActionsAdornment
                  label="Open the video"
                  actions={urlActions(watch('video_url'), 'Open the video')}
                />
              ),
            }}
            {...register('video_url')}
          />

          {/* Where this image came from — in the payload since the AI/URL
              importer landed, and never shown until now. Read-only: the
              backend owns it. */}
          {image?.source_url && (
            <Typography variant="caption" color="text.secondary" component="div">
              Source:{' '}
              <ValueActions actions={urlActions(image.source_url, 'Open the source')}>
                {image.source_url}
              </ValueActions>
            </Typography>
          )}

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              360° panorama
            </Typography>
          </Divider>
          <Controller
            control={control}
            name="is_360_degree"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={!canManage}
                  />
                }
                label="This image is a 360° panorama"
              />
            )}
          />
          {is360 && (
            <Controller
              control={control}
              name="offset_360_degree"
              render={({ field }) => (
                <TextField
                  label="Heading offset (degrees)"
                  type="number"
                  size="small"
                  disabled={!canManage}
                  slotProps={{ htmlInput: { min: 0, max: 360 } }}
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  error={Boolean(errors.offset_360_degree)}
                  helperText={errors.offset_360_degree?.message}
                />
              )}
            />
          )}

          {showInGroup && (
            <>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Group visibility
                </Typography>
              </Divider>
              <Controller
                control={control}
                name="show_in_group"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={!canManage}
                      />
                    }
                    label="Show in itinerary group"
                  />
                )}
              />
            </>
          )}

          {canManage && (
            <>
              <Divider textAlign="left">
                <Typography variant="caption" color="text.secondary">
                  Replace image
                </Typography>
              </Divider>
              <UploadDropzone
                compact
                multiple={false}
                disabled={replacing}
                label="Drag a replacement here"
                onFiles={(files) => {
                  const f = files[0];
                  if (f) onReplace(f);
                }}
              />
              <CropControl src={original} filename={fileNameFor(image)} disabled={replacing} onReplace={onReplace} />
            </>
          )}
        </Stack>

        {canManage && (
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button size="small" onClick={requestClose} disabled={saving}>
              Close
            </Button>
            <ImmediateActionButton type="submit" variant="contained" disabled={saving || !isDirty}>
              {saving ? 'Saving…' : 'Save details now'}
            </ImmediateActionButton>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}

function fileNameFor(image: GalleryImage | null): string {
  return image ? `image-${image.id}` : 'image';
}

/** The crop entry point — a button that opens `ImageCropDialog` and forwards its
 *  produced File to the replace handler. */
function CropControl({
  src,
  filename,
  disabled,
  onReplace,
}: {
  src: string | null;
  filename: string;
  disabled: boolean;
  onReplace: (file: File) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<CropRotateIcon />}
        onClick={() => setOpen(true)}
        disabled={disabled || !src}
      >
        Crop / rotate
      </Button>
      <ImageCropDialog
        open={open}
        src={src}
        filename={filename}
        onCancel={() => setOpen(false)}
        onCropped={(file) => {
          setOpen(false);
          onReplace(file);
        }}
      />
    </>
  );
}
